import { useMemo } from 'react'
import { Box, Group, Text, Tooltip } from '@mantine/core'
import type { Ambiente, Divergencia } from '@/api/ipam'
import { calcularFaixa } from './cidr'

// ============================================================
// Mapa do espaço — uma linha por bloco /16, 16 células por linha (os slots /20).
//
// É a visão que o registro não dá: onde há espaço livre de verdade e onde duas
// VPCs disputam a mesma faixa. O rótulo da célula é o terceiro octeto (0, 16,
// 32, … 240), que é como a convenção do §2.3 nomeia os slots.
//
// Faixas maiores que /20 (um /16 inteiro, por exemplo) pintam TODOS os slots que
// cobrem — senão um /16 ocupado apareceria como um único slot ocupado e 15
// livres, que é o oposto da verdade.
//
// **Espaço público fica FORA.** São 22 faixas, cada uma um /16 inteiro: entrariam
// pintando a linha completa, somariam 18 linhas e não responderiam "onde há /20
// livre", que é a pergunta do mapa. Elas têm a sua própria tabela na tela. Quem
// chama já filtra; o filtro aqui é para o componente valer por si.
// ============================================================

const SLOTS_POR_BLOCO = 16
/** Um slot /20 tem 4096 endereços. */
const TAMANHO_SLOT = 4096

type EstadoSlot =
  | 'livre'
  | 'vivo_roteado'
  | 'vivo'
  | 'rota_indeterminada'
  | 'reservado'
  | 'externo'
  | 'colisao'

/** Precedência ao pintar: o pior achado vence, para colisão não sumir sob "em uso". */
const PRECEDENCIA: EstadoSlot[] = [
  'livre',
  'reservado',
  'externo',
  'rota_indeterminada',
  'vivo',
  'vivo_roteado',
  'colisao',
]

// O texto usa `-light-color`, não `-9`: o `-9` é escuro nos dois temas, e sobre o
// fundo `-light` (que no tema escuro TAMBÉM é escuro) o número do slot fica
// ilegível. `-light-color` é o par que o Mantine mantém legível em ambos.
const CORES: Record<EstadoSlot, { bg: string; borda: string; texto: string; peso: number }> = {
  livre:              { bg: 'var(--mantine-color-default)',      borda: 'var(--mantine-color-default-border)', texto: 'var(--mantine-color-dimmed)',            peso: 400 },
  vivo_roteado:       { bg: 'var(--mantine-color-teal-light)',   borda: 'var(--mantine-color-teal-6)',   texto: 'var(--mantine-color-teal-light-color)',   peso: 700 },
  vivo:               { bg: 'var(--mantine-color-blue-light)',   borda: 'var(--mantine-color-blue-6)',   texto: 'var(--mantine-color-blue-light-color)',   peso: 700 },
  rota_indeterminada: { bg: 'var(--mantine-color-gray-light)',   borda: 'var(--mantine-color-gray-6)',   texto: 'var(--mantine-color-gray-light-color)',   peso: 700 },
  reservado:          { bg: 'var(--mantine-color-yellow-light)', borda: 'var(--mantine-color-yellow-6)', texto: 'var(--mantine-color-yellow-light-color)', peso: 400 },
  externo:            { bg: 'var(--mantine-color-violet-light)', borda: 'var(--mantine-color-violet-6)', texto: 'var(--mantine-color-violet-light-color)', peso: 700 },
  colisao:            { bg: 'var(--mantine-color-red-filled)',   borda: 'var(--mantine-color-red-9)',    texto: 'var(--mantine-color-white)',             peso: 700 },
}

const LEGENDA: { estado: EstadoSlot; rotulo: string }[] = [
  { estado: 'vivo_roteado',       rotulo: 'em uso e roteada no TGW' },
  { estado: 'vivo',               rotulo: 'em uso, sem rota no hub' },
  { estado: 'rota_indeterminada', rotulo: 'em uso, rota não verificada' },
  { estado: 'reservado',          rotulo: 'reservada no registro, sem VPC' },
  { estado: 'externo',            rotulo: 'fora desta organização, alcançável pelo TGW' },
  { estado: 'colisao',            rotulo: 'colisão — duas ou mais VPCs' },
  { estado: 'livre',              rotulo: 'livre' },
]

interface Slot {
  estado: EstadoSlot
  cidr: string
  detalhes: string[]
}

interface Bloco {
  bloco: string
  donos: string[]
  slots: Slot[]
}

function pior(a: EstadoSlot, b: EstadoSlot): EstadoSlot {
  return PRECEDENCIA.indexOf(a) >= PRECEDENCIA.indexOf(b) ? a : b
}

/** Índices dos slots /20 que uma faixa cobre dentro do seu bloco /16. */
function slotsCobertos(cidr: string): number[] {
  const f = calcularFaixa(cidr)
  if (!f.count) return []
  const primeiro = Math.floor(f.terceiroOcteto / 16)
  if (f.prefixo >= 20) return [primeiro]
  // Faixa maior que /20 cobre vários slots. Limitado ao próprio bloco /16: um
  // /8 cobriria 4096 slots e não é isso que a linha do /16 representa.
  const quantos = Math.min(SLOTS_POR_BLOCO, Math.ceil(f.count / TAMANHO_SLOT))
  return Array.from({ length: quantos }, (_, i) => primeiro + i)
    .filter((i) => i < SLOTS_POR_BLOCO)
}

interface Props {
  ambientes: Ambiente[]
  divergencias: Divergencia[]
}

export function MapaDeEspaco({ ambientes, divergencias }: Props) {
  const blocos = useMemo<Bloco[]>(() => {
    const porBloco = new Map<string, { slots: Slot[]; donos: Set<string>; ocupantes: Map<number, string[]> }>()

    const garantir = (bloco16: string) => {
      let b = porBloco.get(bloco16)
      if (!b) {
        b = {
          donos: new Set(),
          ocupantes: new Map(),
          slots: Array.from({ length: SLOTS_POR_BLOCO }, (_, i) => ({
            estado: 'livre' as EstadoSlot,
            cidr: `${bloco16.split('/')[0].split('.').slice(0, 2).join('.')}.${i * 16}.0/20`,
            detalhes: [],
          })),
        }
        porBloco.set(bloco16, b)
      }
      return b
    }

    // 1) realidade: as VPCs que existem. A default da AWS fica fora — 140 delas
    //    compartilham 172.31.0.0/16 e afogariam o mapa.
    for (const a of ambientes) {
      if (a.default || a.estado === 'publico') continue
      const f = calcularFaixa(a.cidr)
      if (!f.count) continue
      const b = garantir(f.bloco16)
      if (a.projeto) b.donos.add(a.projeto)
      const estado: EstadoSlot =
        a.estado === 'rota_indeterminada' ? 'rota_indeterminada'
        : a.estado === 'vivo_roteado' ? 'vivo_roteado'
        : 'vivo'

      for (const i of slotsCobertos(a.cidr)) {
        const slot = b.slots[i]
        const chave = `${a.cidr} · ${a.vpc_nome || a.vpc} · conta ${a.conta}`
        // Duas VPCs distintas no mesmo slot é colisão. Duas faixas da MESMA VPC
        // (VPC com dois CIDRs associados) não é. Comparação EXATA de id, não
        // substring: `vpc-12`.includes(`vpc-1`) é verdadeiro e engoliria a colisão.
        const jaTem = b.ocupantes.get(i) ?? []
        const outroVpc = jaTem.some((vpc) => vpc !== a.vpc)
        b.ocupantes.set(i, [...jaTem, a.vpc])
        slot.estado = outroVpc ? 'colisao' : pior(slot.estado, estado)
        slot.detalhes = [...slot.detalhes, chave]
      }
    }

    // 2) eixo declarado: reservado sem VPC, e o que vive fora da organização.
    for (const d of divergencias) {
      const alvo =
        d.tipo === 'registro_sem_vpc' ? 'reservado'
        : d.tipo === 'fora_da_organizacao' ? 'externo'
        : null
      if (!alvo) continue
      const f = calcularFaixa(d.cidr)
      if (!f.count) continue
      const b = garantir(f.bloco16)
      if (d.detalhe.projeto) b.donos.add(d.detalhe.projeto)
      const rotulo = [d.detalhe.projeto, d.detalhe.ambiente].filter(Boolean).join('/')
      for (const i of slotsCobertos(d.cidr)) {
        const slot = b.slots[i]
        slot.estado = pior(slot.estado, alvo as EstadoSlot)
        slot.detalhes = [
          ...slot.detalhes,
          `${d.cidr} · ${rotulo || 'declarado'} · ${alvo === 'reservado' ? 'sem VPC na faixa' : 'fora da organização'}`,
        ]
      }
    }

    return [...porBloco.entries()]
      .map(([bloco, b]) => ({ bloco, donos: [...b.donos].sort(), slots: b.slots }))
      .sort((x, y) => {
        // Ordem numérica por octeto, não lexicográfica: senão 10.9 vem depois de
        // 10.20 e a leitura do mapa fica sem sentido.
        const a = x.bloco.split('/')[0].split('.').map(Number)
        const c = y.bloco.split('/')[0].split('.').map(Number)
        return a[0] - c[0] || a[1] - c[1]
      })
  }, [ambientes, divergencias])

  if (!blocos.length) {
    return (
      <Text size="sm" c="dimmed">
        Nenhum bloco com ocupação conhecida. O mapa aparece depois da primeira coleta.
      </Text>
    )
  }

  return (
    <Box>
      <Group gap="xs" mb="sm" style={{ rowGap: 6 }}>
        {LEGENDA.map(({ estado, rotulo }) => (
          <Group key={estado} gap={6} wrap="nowrap" mr="md">
            <Box
              style={{
                width: 12, height: 12, borderRadius: 2,
                background: CORES[estado].bg,
                border: `1px solid ${CORES[estado].borda}`,
              }}
            />
            <Text size="xs" c="dimmed" ff="monospace">{rotulo}</Text>
          </Group>
        ))}
      </Group>

      <Box style={{ overflowX: 'auto' }}>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 640 }}>
          {blocos.map((b) => (
            <Box
              key={b.bloco}
              style={{
                display: 'grid', gridTemplateColumns: '132px 1fr', gap: 12,
                alignItems: 'center', padding: '5px 8px', borderRadius: 3,
              }}
            >
              <Box style={{ minWidth: 0 }}>
                <Text size="xs" ff="monospace" fw={600}>{b.bloco}</Text>
                <Text size="10px" c="dimmed" truncate title={b.donos.join(', ') || '—'}>
                  {b.donos.join(', ') || '—'}
                </Text>
              </Box>
              <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(16, 1fr)', gap: 2 }}>
                {b.slots.map((s, i) => {
                  const cor = CORES[s.estado]
                  const dica = s.detalhes.length
                    ? `${s.cidr}\n${s.detalhes.join('\n')}`
                    : `${s.cidr}\nlivre`
                  return (
                    <Tooltip
                      key={i}
                      label={<Text size="xs" style={{ whiteSpace: 'pre-line' }}>{dica}</Text>}
                      withArrow
                      multiline
                      w={320}
                    >
                      <Box
                        style={{
                          height: 30, borderRadius: 2, background: cor.bg,
                          border: `1px solid ${cor.borda}`, color: cor.texto,
                          fontFamily: 'monospace', fontSize: 9.5, fontWeight: cor.peso,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'default',
                        }}
                      >
                        {i * 16}
                      </Box>
                    </Tooltip>
                  )
                })}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
